import "./quote-board-group-header.css";

import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { format } from "date-fns";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";
import { getTimeZoneShortName } from "~/shared/utils/time-utils.ts";

import { canTenderQuoteBoardRow } from "../../../utils/quote-board-status.ts";

type GroupedDataItem = {
	field: string;
	items: QuoteBoardItem[];
	value: unknown;
};

type ToggleBatchSelection = (
	bulkQuoteId: string,
	selected: boolean,
	visibleQuoteIds: readonly number[],
) => void;

type QuoteBoardGroupHeaderProps = GridCustomCellProps & {
	selectedQuoteIds?: ReadonlySet<number>;
	onToggleBatchSelection?: ToggleBatchSelection;
	showBulkTenderSelection?: boolean;
	isInternal?: boolean;
};

type GroupCreatedDate = {
	value: string | null;
	parseAsUtc: boolean;
};

const getGroupCreatedDate = (item: QuoteBoardItem): GroupCreatedDate => {
	if (item.bulkQuoteId === null) {
		return { parseAsUtc: false, value: item.createdDate };
	}
	if (item.bulkReceivedAtUtc) {
		return { parseAsUtc: false, value: item.bulkReceivedAtUtc };
	}

	// Keep legacy bulk rows compatible until every Quote Board response includes
	// the explicit batch receipt timestamp. New bulk-tender rows use local
	// DateTime.Now values; older bulk quote rows were serialized as UTC-like values.
	return { parseAsUtc: item.isLtlTender !== true, value: item.createdDate };
};

function parseCreatedDate(groupDate: GroupCreatedDate): Date {
	if (!groupDate.value) return new Date(Number.NaN);
	const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(groupDate.value);
	const dateString =
		groupDate.parseAsUtc && !hasOffset
			? `${groupDate.value}Z`
			: groupDate.value;
	return new Date(dateString);
}

function formatCreatedDate(groupDate: GroupCreatedDate): string {
	if (!groupDate.value) return "N/A";
	const date = parseCreatedDate(groupDate);
	if (date.getUTCFullYear() <= 1) return "N/A";
	return `${format(date, "MM/dd/yyyy hh:mm a")} ${getTimeZoneShortName(date)}`;
}

const getBatchSelection = (
	items: QuoteBoardItem[],
	selectedQuoteIds?: ReadonlySet<number>,
	isInternal?: boolean,
) => {
	const eligibleItems = items.filter((item) =>
		canTenderQuoteBoardRow(item, { isInternal }),
	);
	return {
		eligibleQuoteIds: eligibleItems.map((item) => item.quoteId),
		eligibleCount: eligibleItems.length,
		allEligibleSelected:
			eligibleItems.length > 0 &&
			eligibleItems.every((item) => selectedQuoteIds?.has(item.quoteId)),
	};
};

const BatchSelectionCheckbox = ({
	bulkQuoteId,
	checked,
	disabled,
	visibleQuoteIds,
	onToggleBatchSelection,
}: {
	bulkQuoteId: string;
	checked: boolean;
	disabled: boolean;
	visibleQuoteIds: readonly number[];
	onToggleBatchSelection?: ToggleBatchSelection;
}) => (
	<Checkbox
		aria-label={`Select all eligible shipments in batch ${bulkQuoteId}`}
		checked={checked}
		disabled={disabled}
		onChange={(event) =>
			onToggleBatchSelection?.(bulkQuoteId, event.value, visibleQuoteIds)
		}
	/>
);

const getEarliestItem = (
	items: QuoteBoardItem[],
): QuoteBoardItem | undefined => {
	let earliestItem: QuoteBoardItem | undefined;
	for (const item of items) {
		const itemCreatedDate = getGroupCreatedDate(item);
		const earliestCreatedDate = earliestItem
			? getGroupCreatedDate(earliestItem)
			: null;
		const itemTimestamp = parseCreatedDate(itemCreatedDate).getTime();
		const earliestTimestamp = earliestCreatedDate
			? parseCreatedDate(earliestCreatedDate).getTime()
			: Number.POSITIVE_INFINITY;
		if (
			!earliestItem ||
			!earliestCreatedDate ||
			itemTimestamp < earliestTimestamp
		) {
			earliestItem = item;
		}
	}
	return earliestItem;
};

export const QuoteBoardGroupHeader = ({
	selectedQuoteIds,
	onToggleBatchSelection,
	showBulkTenderSelection = false,
	isInternal = false,
	...props
}: QuoteBoardGroupHeaderProps) => {
	const dataItem = props.dataItem as GroupedDataItem;

	if (!dataItem) {
		return null;
	}

	const subItems = dataItem.items;
	// In some cases (multi-level grouping), items might be nested.
	// For QuoteBoardGrid we expect a flat array of QuoteBoardItems here.
	const nonExceptionItems = subItems.filter(
		(item) => item.quoteId !== 0 && item.tabType !== "Exception",
	);
	const summaryItems =
		nonExceptionItems.length > 0 ? nonExceptionItems : subItems;
	const earliestItem = getEarliestItem(summaryItems);
	if (!earliestItem) {
		return <GridTd tdProps={props.tdProps}>{props.children}</GridTd>;
	}

	const dateStr = formatCreatedDate(getGroupCreatedDate(earliestItem));

	const statsItem =
		earliestItem.requestCount != null ||
		earliestItem.processedCount != null ||
		earliestItem.errorCount != null
			? earliestItem
			: (summaryItems.find(
					(item) =>
						item.requestCount != null ||
						item.processedCount != null ||
						item.errorCount != null,
				) ?? earliestItem);

	const requestCount = statsItem.requestCount ?? 0;
	const processedCount = statsItem.processedCount ?? 0;
	const errorCount = statsItem.errorCount ?? 0;
	const isBulk = subItems.some((item) => item.bulkQuoteId != null);
	const { eligibleCount, eligibleQuoteIds, allEligibleSelected } =
		getBatchSelection(subItems, selectedQuoteIds, isInternal);

	return (
		<GridTd
			className={`quote-board-group-header-cell ${isBulk ? "" : "non-bulk-group-header"}`}
			tdProps={props.tdProps}
		>
			{props.children ? (
				<div className={"k-reset quote-board-group-header-content"}>
					{showBulkTenderSelection && earliestItem.bulkQuoteId ? (
						<BatchSelectionCheckbox
							bulkQuoteId={earliestItem.bulkQuoteId}
							checked={allEligibleSelected}
							disabled={eligibleCount === 0}
							onToggleBatchSelection={onToggleBatchSelection}
							visibleQuoteIds={eligibleQuoteIds}
						/>
					) : null}
					<div className={"quote-board-group-header-expander"}>
						{props.children}
					</div>
					{isBulk ? (
						<div className={"quote-board-group-header-details"}>
							<span>
								<b>{dateStr}</b> - Bulk Upload
							</span>
							<span>
								Requested: {requestCount} / Processed: {processedCount} /{" "}
								<span className={errorCount > 0 ? "error-text" : ""}>
									Errored: {errorCount}
								</span>
							</span>
						</div>
					) : null}
				</div>
			) : null}
		</GridTd>
	);
};
